// 100ステージ（10章×10）。章ごとに地形・陣地ルール・レイアウトを変え、章の敵を配置する。
// STAGES は公開情報（StageInfo）、stageDef は戦闘が使う詳細（設定の上書きを反映するため毎回計算）。
import { RULES_VERSION, type StageInfo } from '../types';
import { CHAPTERS, ENEMY_LIST } from '../data/enemies';
import { CHAPTER_LEADERS } from '../data/leaders';
import { CFG, type RivalAiKey, type RivalTraits } from './config';
import type { AiStyle } from './ai';
import { PROLOGUE } from './prologue';
import { STAGE_TUNE } from './stage-tune';

/**
 * レイアウト。上半分（0〜5行）だけを書き、6〜10行は180°回転して作る（C/H は持ち主が入れ替わる）。
 * . 草地  # 岩  C 本陣  H 帰還マス  a 小型キャンプ  m 中型  e エリート（章内4番目以降）
 * B 中央キャンプ（龍脈）  $ 宝箱  + 祠（回復）
 */
export const LAYOUTS: Record<string, string[]> = {
  meadow: ['#....#', '.#.C.#.', 'a..H.$', '..#..m.', '.+..#.', 'e.#B#.e'],
  ruins: ['#.##.#', '#..C..#', '.a.H.m', '.#...#.', '$..a..', 'e#.B.#e'],
  volcano: ['#....#', '.#.C.#.', '.a.H#.', 'm..#.a.', '#.$..+', 'e#.B.#e'],
  crossing: ['#....#', '.#.C.#.', 'a..H.$', '.#m.#..', '..+...', 'e..B..e'],
  corridor: ['#....#', '..#C#..', '.a.H.m', '.#...#.', '.$..+.', 'e..B..e'],
  river: ['#....#', '.#.C.#.', 'm..H.a', '..##...', '.$..+.', 'e#.B.#e'],
  fort: ['#....#', '.#.C.#.', 'm..H.a', '..#.#..', '.+..$.', 'e..B..e'],
  wide: ['#.##.#', '..#C#..', 'a..H.m', '.......', '#.$.+#', 'e.#B#.e'],
};

export type CampTier = 'small' | 'medium' | 'elite';
export interface CampFill {
  enemyId: string;
  tier: CampTier;
}

export interface StageDef {
  id: number;
  chapter: number;
  index: number;
  name: string;
  terrain: string;
  layout: string;
  rule: string;
  coreHp: number;
  coreDef: number;
  /** レイアウトの記号 → 置く敵（null なら草地にする） */
  camps: Record<'a' | 'm' | 'e' | 'B', CampFill | null>;
  guardian: { enemyId: string; tier: 'boss' | 'elite'; pattern: 'fan' | 'line' | 'burst' } | null;
  rival: { enemyId: string; lv: number; power: number; template: number };
  waves: number[];
  raiderId: string | null;
  difficulty: number;
  /** 敵の能力の基準にする面（通常は id。上の難易度は第10章の同じ番号の面＝90+index） */
  statStage: number;
  /** 敵の HP・攻撃・本陣HPに掛ける倍率（通常 1。上の難易度は hardScale） */
  scale: number;
  /** 特別な戦い（defense＝防衛戦：敵ヒーローなし・波で攻めてくる） */
  mode?: 'defense';
  /** 防衛戦の攻め手（章の妖） */
  attackers?: { small: string[]; medium: string[]; elite: string[] };
}

/** 防衛戦のステージか */
export const isDefenseStage = (stageId: number) => {
  const chapter = Math.ceil(stageId / 10);
  return CFG.defense.chapters.includes(chapter) && stageId - (chapter - 1) * 10 === CFG.defense.index;
};

/** 面ごとの敵の強さの倍率（CFG.curveTune：章の倍率 × 面の上書き × STAGE_TUNE） */
export function stageTune(stageId: number): number {
  const chapter = Math.min(10, Math.max(1, Math.ceil(stageId / 10)));
  return (CFG.curveTune.chapter[chapter - 1] ?? 1) * (CFG.curveTune.stage[stageId] ?? 1) * (STAGE_TUNE[stageId] ?? 1);
}

/**
 * 上の難易度の敵の強さ（BALANCE.md 56章）。上の第N陣は、敵の顔ぶれ・盤面・決まりはその章のまま、能力は第10章の同じ番号の面（90+index）を基準にして
 * band[d-1] の [第1陣, 第100陣] を対数で直線補間した倍率を掛ける（上の1章＝通常の第10章並み → 上の第100陣で band の上端）。
 * 章ごとの顔ぶれの差（序盤の章は敵の種類・数が少ない）は hardTune[章] で埋める（推奨1.0倍で勝率が通常並みになるように測った値）
 */
export function hardScale(stageId: number, difficulty: number): number {
  const d = Math.floor(difficulty);
  if (d <= 0) return 1;
  const H = CFG.difficulty;
  const [lo, hi] = H.band[Math.min(d, H.band.length) - 1];
  const t = (stageId - 1) / 99;
  return Math.exp(Math.log(lo) + (Math.log(hi) - Math.log(lo)) * t);
}
export const hardTune = (stageId: number, difficulty: number) => (difficulty > 0 ? (CFG.difficulty.hardTune[Math.ceil(stageId / 10) - 1] ?? 1) : 1);

/** 敵の強さの基準曲線（CFG.curve）のステージ stageId での値。章内は [1面, 10面] を対数で直線補間し、stageTune を掛ける */
export function curveAt(stageId: number, key: 'hp' | 'atk'): number {
  const chapter = Math.min(10, Math.max(1, Math.ceil(stageId / 10)));
  const [a, z] = CFG.curve[key][chapter - 1];
  const t = (stageId - (chapter - 1) * 10 - 1) / 9;
  return Math.exp(Math.log(a) + (Math.log(z) - Math.log(a)) * Math.max(0, Math.min(1, t))) * stageTune(stageId);
}

const KANJI = ['一', '二', '三', '四', '五', '六', '七', '八', '九', '十'];

function chapterRoster(chapter: number) {
  const list = ENEMY_LIST.filter((e) => e.chapter === chapter);
  const lead = CHAPTER_LEADERS[chapter];
  return {
    small: list.filter((e) => e.role === 'small').map((e) => e.id),
    medium: list.filter((e) => e.role === 'medium').map((e) => e.id),
    elite: list.filter((e) => e.role === 'elite').map((e) => e.id),
    boss: lead?.boss ?? list.find((e) => e.role === 'boss')!.id,
    rival: lead?.rival ?? list.find((e) => e.role === 'rival')!.id,
    /** 攻城の妖（増援に使う） */
    siege: list.find((e) => e.type === 'siege')?.id ?? null,
  };
}

/** プロローグ（三百年前・天下統一の最終戦）のステージID。画面だけで遊ぶ（サーバーを通さず、報酬なし） */
export const PROLOGUE_STAGE = 0;

/**
 * プロローグの戦場：人間の軍（PROLOGUE.foes。setup.ts が置く）と敵本陣だけ。番人・結界は後の戦場で扱う。
 * 操作するのは酒呑童子（setup.ts で絵と名前を差し替える）。
 */
function prologueDef(): StageDef {
  const base = stageDef(7);
  return {
    ...base,
    id: PROLOGUE_STAGE,
    name: '人の世の最後の城',
    terrain: 'capital',
    layout: 'wide',
    camps: { a: null, m: null, e: null, B: null },
    guardian: null,
    rival: { enemyId: 'h10', lv: 1, power: 0.45, template: 0 },
    waves: [],
    raiderId: null,
    coreHp: Math.round(base.coreHp * PROLOGUE.coreHp),
  };
}

export function stageDef(stageId: number, difficulty = 0): StageDef {
  if (stageId === PROLOGUE_STAGE) return prologueDef();
  if (!Number.isInteger(stageId) || stageId < 1 || stageId > 100) throw new Error(`stage ${stageId}`);
  const chapter = Math.ceil(stageId / 10);
  const index = stageId - (chapter - 1) * 10;
  const ch = CHAPTERS[chapter - 1];
  const R = chapterRoster(chapter);
  const i = index;
  const layouts = CFG.chapterLayouts[chapter - 1];
  const rules = CFG.chapterRules[chapter - 1];
  const camps: StageDef['camps'] = {
    a: { enemyId: R.small[(i - 1) % 4], tier: 'small' },
    B: i <= 2 ? { enemyId: R.small[(i + 1) % 4], tier: 'small' } : { enemyId: R.medium[i % 3], tier: 'medium' },
    m: i < 2 ? null : i === 2 ? { enemyId: R.small[(i + 2) % 4], tier: 'small' } : { enemyId: R.medium[(i + 1) % 3], tier: 'medium' },
    e: i < 4 ? null : { enemyId: R.elite[i % 2], tier: 'elite' },
  };
  const patterns = CFG.bossPatterns;
  const guardian: StageDef['guardian'] =
    i === 10
      ? { enemyId: R.boss, tier: 'boss', pattern: patterns[(chapter - 1) % patterns.length]}
      : i >= CFG.guardianFrom
        ? { enemyId: R.elite[(i + 1) % 2], tier: 'elite', pattern: 'fan' }
        : null;
  const d = Math.max(0, Math.floor(difficulty));
  // 上の難易度は能力の基準を第10章の同じ番号の面に（sc＝基準の章）
  const statStage = d > 0 ? 90 + i : stageId;
  const sc = d > 0 ? 10 : chapter;
  const scale = hardScale(stageId, d);
  const lv = 1 + CFG.rival.lvPerChapter * (sc - 1) + Math.floor((CFG.rival.lvInChapter * (i - 1)) / 9) + d * CFG.difficulty.rivalLv;
  const power =
    (sc === 1 ? (CFG.rival.earlyPower[i - 1] ?? 1) : 1) *
    (CFG.rival.chapterPower[sc - 1] ?? 1) *
    (1 + CFG.rival.perIndex * (i - 1)) *
    (i === 10 ? (CFG.rival.bossStage[sc - 1] ?? 1) : 1);
  const coreHp = Math.round(
    (CFG.coreHp.base + CFG.coreHp.perIndex * (i - 1)) * (CFG.coreHp.chapterMult[sc - 1] ?? 1) * (i >= 9 ? (CFG.coreHp.castleMult[sc - 1] ?? 1) : 1) * curveAt(statStage, 'atk') * scale,
  );
  const defense = isDefenseStage(stageId);
  const waves = defense ? [] : (CFG.waves[i] ?? []);
  return {
    id: stageId,
    chapter,
    index,
    name: `${ch.name} 其の${KANJI[index - 1]}`,
    terrain: ch.terrain,
    layout: layouts[(i - 1) % layouts.length],
    rule: rules[(i - 1) % rules.length],
    coreHp,
    coreDef: Math.round(CFG.coreDef.base * curveAt(statStage, 'atk')),
    camps,
    guardian,
    rival: { enemyId: R.rival, lv, power, template: chapter - 1 },
    waves,
    // 増援：攻城の章からは偶数面が攻城の妖
    raiderId: waves.length ? (R.siege && chapter >= CFG.enemyTypes.siege.fromChapter && i % 2 === 0 ? R.siege : R.elite[i % 2]) : null,
    difficulty: d,
    statStage,
    scale,
    ...(defense ? { mode: 'defense' as const, attackers: { small: R.small, medium: R.medium, elite: R.elite } } : {}),
  };
}

/** そのステージに出てくる敵ID（rival を含む。図鑑・ドロップの対象） */
export function stageEnemyIds(def: StageDef): string[] {
  const layout = LAYOUTS[def.layout].join('');
  const ids: string[] = [];
  for (const k of ['B', 'a', 'm', 'e'] as const) {
    const fill = def.camps[k];
    if (fill && layout.includes(k) && !ids.includes(fill.enemyId)) ids.push(fill.enemyId);
  }
  if (def.guardian && !ids.includes(def.guardian.enemyId)) ids.push(def.guardian.enemyId);
  if (def.raiderId && !ids.includes(def.raiderId)) ids.push(def.raiderId);
  if (def.attackers) for (const id of [...def.attackers.small, ...def.attackers.medium, ...def.attackers.elite]) if (!ids.includes(id)) ids.push(id);
  if (def.mode !== 'defense') ids.push(def.rival.enemyId);
  return ids;
}

function buildStages(): StageInfo[] {
  const out: StageInfo[] = [];
  for (let id = 1; id <= 100; id++) {
    const d = stageDef(id);
    out.push({
      id,
      chapter: d.chapter,
      index: d.index,
      name: d.name,
      enemyIds: stageEnemyIds(d),
      bossId: d.guardian?.enemyId ?? null,
      isChapterBoss: d.index === 10,
      terrain: d.terrain,
      enemyLevel: d.rival.lv,
      ...(d.mode ? { mode: d.mode } : {}),
      ...(rivalAiOf(id) ? { rivalAi: rivalAiOf(id)! } : {}),
    });
  }
  return out;
}

/** 砦（章の9番目）の手番の上限（ルールの版3から。画面の札用。ほかの戦場は null） */
export const castleTurnLimitOf = (stageId: number, rules = RULES_VERSION): number | null =>
  rules >= 3 && stageId > 0 && stageId % 10 === 9 && !isDefenseStage(stageId) ? CFG.v3.castleTurnLimit : null;

/** 敵の武将の傾向（章内の番号 1〜9。1-1・章ボスの面・防衛戦は null） */
export function rivalAiOf(stageId: number): RivalAiKey | null {
  const ch = Math.ceil(stageId / 10);
  const idx = ((stageId - 1) % 10) + 1;
  // 最初の戦場（1-1）・章ボスの面・防衛戦は通常のAI
  if (stageId === 1 || idx === 10 || isDefenseStage(stageId)) return null;
  const o = CFG.rivalAi.order;
  return o[(idx - 1 + ch - 1) % o.length];
}

/**
 * 敵の武将の傾向の中身（立ち回り・建てられる数の上乗せ）。無ければ null。
 * ルールの版3からは CFG.v3.rivalAi の差し替え（本陣速攻・武将狙いが強い）と性質（traits。武将の能力の倍率・技の旗。BALANCE.md 40章）
 */
export const rivalAiType = (stageId: number, rules = RULES_VERSION): { name: string; style: AiStyle; aggro?: number; buildMax?: number; traits?: RivalTraits } | null => {
  const k = rivalAiOf(stageId);
  if (!k) return null;
  const t = CFG.rivalAi.types[k];
  const v3 = rules >= 3 ? CFG.v3.rivalAi[k] : undefined;
  if (!v3) return t;
  const out: NonNullable<ReturnType<typeof rivalAiType>> = { ...t, style: v3.style ?? t.style, aggro: v3.aggro ?? t.aggro };
  if (v3.traits) {
    out.traits = v3.traits;
    if (v3.traits.buildMax) out.buildMax = (t.buildMax ?? 0) + v3.traits.buildMax;
  }
  return out;
};

/** 100ステージの公開情報 */
export const STAGES: StageInfo[] = buildStages();
